import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userApi } from '../../api/services';
import { User, CreateUserRequest } from '../../types';

// 状态类型定义
interface UserState {
  users: User[];
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: UserState = {
  users: [],
  loading: false,
  error: null,
};

// 异步获取所有用户
export const fetchAllUsers = createAsyncThunk(
  'users/fetchAllUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userApi.getAllUsers();
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.message || '获取用户列表失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取用户列表失败');
    }
  }
);

// 异步创建新用户
export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: CreateUserRequest, { rejectWithValue }) => {
    try {
      const response = await userApi.createUser(userData);
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.message || '创建用户失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建用户失败');
    }
  }
);

// 异步删除用户
export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await userApi.deleteUser(userId);
      if (response.data.success) {
        return userId;
      } else {
        return rejectWithValue(response.data.message || '删除用户失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '删除用户失败');
    }
  }
);

// 异步修改用户密码
export const changePassword = createAsyncThunk(
  'users/changePassword',
  async ({ userId, newPassword }: { userId: string, newPassword: string }, { rejectWithValue }) => {
    try {
      await userApi.changePassword(userId, newPassword);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '修改密码失败');
    }
  }
);

// 用户管理切片
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 处理获取所有用户的各种状态
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理创建用户的各种状态
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理删除用户的各种状态
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理修改密码的状态
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = userSlice.actions;
export default userSlice.reducer; 